import dayjs from '@/config/dayjs';
import {
  DiversificationCategory,
  HistoricalDiversificationCategory,
} from '@/store/diversificationsApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { convertNumberToPercentage } from '@/utils/numbers';
import { PortfolioDiversificationIdNumber } from '@/utils/enums';
import { normalizeSearchText } from '@/utils/string';

// ── Types ──────────────────────────────────────────────────────────────

export type DisplayMode = 'dollar' | 'percentage';

export interface AssetAllocationDataRow {
  date: Date;
  dateLabel?: string;
  [categoryKey: string]: number | Date | string | undefined;
}

// ── Constants ──────────────────────────────────────────────────────────

/** Root diversification scheme id for portfolio asset-allocation chart queries. */
export const DIVERSIFICATION_ID = PortfolioDiversificationIdNumber.ROOT;
export const CHART_HEIGHT = 550;

const DATE_LABEL_MONTH_THRESHOLD = 3;
const MS_PER_APPROX_MONTH = 30 * 24 * 60 * 60 * 1000;
/** dayjs format tokens (not date-fns) — used with `dayjs().format()`. */
const DATE_FORMAT_SHORT = 'MMM D';
const DATE_FORMAT_LONG = 'MMM YYYY';
export const TOOLTIP_DATE_FORMAT = 'MMMM D, YYYY';
export const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

// ── Utility Functions ──────────────────────────────────────────────────

/**
 * Resolves the canonical category key by matching a historical record's name
 * against the diversification categories (case-insensitive).
 */
export function getCategoryKey(
  categories: DiversificationCategory[],
  histRecName: string,
): string {
  const normalizedHistRecName = normalizeSearchText(histRecName);
  const match = categories.find(
    (cat) => normalizeSearchText(cat.Name) === normalizedHistRecName,
  );
  return match?.Name ?? histRecName;
}

/**
 * Returns the start-of-day and end-of-day timestamps for a date range.
 */
function getDateRangeMs(startDate: Date, endDate: Date) {
  const startMs = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
  ).getTime();
  const endMs = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
    23,
    59,
    59,
    999,
  ).getTime();
  return { startMs, endMs };
}

/**
 * Builds a date-keyed map from historical diversification records,
 * aggregating values per category per date.
 */
function buildDateMap(
  histRecords: HistoricalDiversificationCategory[],
  categories: DiversificationCategory[],
): Record<string, AssetAllocationDataRow> {
  const dateMap: Record<string, AssetAllocationDataRow> = {};

  histRecords.forEach((histRec) => {
    const categoryKey = getCategoryKey(categories, histRec.name);
    histRec.data.forEach((pt) => {
      const dateStr = pt.Date.slice(0, 10);
      if (!dateMap[dateStr]) {
        const [year, month, day] = dateStr.split('-').map(Number);
        dateMap[dateStr] = { date: new Date(year, month - 1, day) };
      }
      const existing = (dateMap[dateStr][categoryKey] as number) ?? 0;
      dateMap[dateStr][categoryKey] = existing + pt.Value;
    });
  });

  return dateMap;
}

/**
 * Converts a date map to a sorted array and filters to the given date range.
 */
function filterAndSortByDateRange(
  dateMap: Record<string, AssetAllocationDataRow>,
  startDate: Date,
  endDate: Date,
): AssetAllocationDataRow[] {
  const { startMs, endMs } = getDateRangeMs(startDate, endDate);

  return Object.values(dateMap)
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .filter((row) => {
      const t = row.date.getTime();
      return t >= startMs && t <= endMs;
    });
}

/**
 * Adds a formatted `dateLabel` to each row based on the data's time span.
 * Uses "MMM d" for ranges <= 3 months, "MMM yyyy" for longer ranges.
 */
function addDateLabels(
  rows: AssetAllocationDataRow[],
): AssetAllocationDataRow[] {
  if (rows.length < 2) {
    return rows.map((row) => ({
      ...row,
      dateLabel: dayjs(row.date).format(DATE_FORMAT_LONG),
    }));
  }

  const rangeMonths =
    (rows[rows.length - 1].date.getTime() - rows[0].date.getTime()) /
    MS_PER_APPROX_MONTH;
  const labelFormat =
    rangeMonths > DATE_LABEL_MONTH_THRESHOLD
      ? DATE_FORMAT_LONG
      : DATE_FORMAT_SHORT;

  return rows.map((row) => ({
    ...row,
    dateLabel: dayjs(row.date).format(labelFormat),
  }));
}

/**
 * Converts each row's numeric category values to percentages of the row total.
 */
function convertRowsToPercentages(
  rows: AssetAllocationDataRow[],
): AssetAllocationDataRow[] {
  return rows.map((row) => {
    const newRow = { ...row };
    let rowTotal = 0;

    for (const [key, val] of Object.entries(newRow)) {
      if (key !== 'date' && key !== 'dateLabel' && typeof val === 'number') {
        rowTotal += val;
      }
    }

    if (rowTotal > 0) {
      for (const key of Object.keys(newRow)) {
        if (
          key !== 'date' &&
          key !== 'dateLabel' &&
          typeof newRow[key] === 'number'
        ) {
          newRow[key] = ((newRow[key] as number) / rowTotal) * 100;
        }
      }
    }

    return newRow;
  });
}

/**
 * Builds the full asset allocation dataset from raw API data.
 * Combines date mapping, sorting, filtering, label generation, and
 * optional percentage conversion into a single pipeline.
 */
export function buildAssetAllocationDataset(
  histRecords: HistoricalDiversificationCategory[],
  categories: DiversificationCategory[],
  startDate: Date,
  endDate: Date,
  displayMode: DisplayMode,
): AssetAllocationDataRow[] {
  const dateMap = buildDateMap(histRecords, categories);
  let rows = filterAndSortByDateRange(dateMap, startDate, endDate);
  rows = addDateLabels(rows);

  if (displayMode === 'percentage') {
    rows = convertRowsToPercentages(rows);
  }

  return rows;
}

/**
 * Extracts category names from the dataset and sorts them by value in the
 * last row (ascending, for proper stacking order in the chart).
 */
export function getSortedCategoryNames(
  dataSet: AssetAllocationDataRow[],
): string[] {
  if (!dataSet.length) return [];

  const names = Object.keys(dataSet[0]).filter(
    (k) => k !== 'date' && k !== 'dateLabel',
  );
  const lastRow = dataSet[dataSet.length - 1];

  return [...names].sort((a, b) => {
    const valA = (lastRow[a] as number) ?? 0;
    const valB = (lastRow[b] as number) ?? 0;
    return valA - valB;
  });
}

/**
 * Builds a map of category name to color from historical records.
 */
export function buildCategoryColorMap(
  histRecords: HistoricalDiversificationCategory[],
  categories: DiversificationCategory[],
): Record<string, string> {
  const map: Record<string, string> = {};
  histRecords.forEach((histRec) => {
    const categoryKey = getCategoryKey(categories, histRec.name);
    if (histRec.color) map[categoryKey] = histRec.color;
  });
  return map;
}

/**
 * Sums all numeric category values in a dataset row.
 */
export function getRowTotal(row: AssetAllocationDataRow): number {
  return Object.entries(row).reduce((sum, [key, val]) => {
    if (key === 'date' || key === 'dateLabel') return sum;
    return sum + (typeof val === 'number' ? val : 0);
  }, 0);
}

// ── Value Formatters ───────────────────────────────────────────────────

/**
 * Formats a value for display based on the current display mode.
 * Used for summary totals (start/end values in the chart header).
 */
export function formatAllocationValue(
  value: number | null,
  displayMode: DisplayMode,
): string {
  if (value === null) return '';
  if (displayMode === 'percentage') {
    return convertNumberToPercentage(value / 100);
  }
  return formatNumberToCurrency(value);
}

/**
 * Formats a series value for tooltip display. Dollar amounts use 2 decimal
 * places for precision; percentages use the standard percentage formatter.
 */
export function formatSeriesValue(
  value: number | null,
  displayMode: DisplayMode,
): string {
  if (value === null) return '';
  if (displayMode === 'percentage') {
    return convertNumberToPercentage(value / 100);
  }
  return formatNumberToCurrency(value, undefined, 2);
}

/**
 * Formats a y-axis value with abbreviated notation for large dollar amounts
 * (e.g. $1.5M, $250k).
 */
export function formatAxisValue(
  value: number,
  displayMode: DisplayMode,
): string {
  if (displayMode === 'percentage') {
    return convertNumberToPercentage(value / 100);
  }
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}k`;
  }
  return formatNumberToCurrency(value, 'USD', 0);
}
